import { FC, useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { Button, IconButton, Typography } from '@mui/material';
import ScheduleAvailTypeTabs from '@/components/Focus/AvailsSchedule/ScheduleAvailTypeTabs';
import { uniq } from 'lodash';
import Modal from '@/components/_common/Modal/Modal';
import { PolarisIcon } from '@/exports/columnCommonComponents';
import { ScheduleAvailType, ScheduleAvailRights } from '@/redux/slices/slice_types';
import RightsUtilizationProgress from './RightsUtilizationProgressBar';
import RightsSelectionSection from './RightsSelectionSection';
import { FocusRightsPossibleResponse } from '@/redux/slices/focusNewScheduleSlice';
import { useAppSelector } from '@/redux/hooks';
import { selectAudioLanguages, selectSubtitlesLanguages } from '@/redux/selectors/constantsSelectors';
import { territoryGrouping } from '@/components/Focus/constants';
import { FocusTheme } from '@/components/Focus/configs/theme';

export type NewScheduleAvailSelectionModalProps = {
  initialSelectedByTab?: Partial<ScheduleAvailRights>;
  initialTab?: ScheduleAvailType;
  onClose: (e: any) => void;
  onSave?: (next: ScheduleAvailRights) => void;
  open: boolean;
  rightsPossible?: FocusRightsPossibleResponse['data'];
  scrollToElementName?: string;
  onTransitionExited?: () => void;
};

const NewScheduleAvailSelectionModal: FC<NewScheduleAvailSelectionModalProps> = ({
  initialSelectedByTab,
  initialTab = 'audio',
  onClose,
  onSave,
  open,
  rightsPossible,
  scrollToElementName,
  onTransitionExited
}) => {
  const audioOptions = useAppSelector(selectAudioLanguages);
  const timedTextOptions = useAppSelector(selectSubtitlesLanguages);

  const [tab, setTab] = useState<ScheduleAvailType>(initialTab);

  const [selectedByTab, setSelectedByTab] = useState<ScheduleAvailRights>({
    audio: uniq(initialSelectedByTab?.audio ?? []),
    territories: uniq(initialSelectedByTab?.territories ?? []),
    timedtext: uniq(initialSelectedByTab?.timedtext ?? [])
  });

  const dubRights = useMemo(() => {
    if (!rightsPossible) return [];
    return Array.from(
      new Set(
        Object.values(rightsPossible.territories)
          .flatMap((territory) => territory.audio)
          .map((item) => item.language)
      )
    ).sort((a, b) => a.localeCompare(b));
  }, [rightsPossible]);

  const subRights = useMemo(() => {
    if (!rightsPossible) return [];
    return Array.from(
      new Set(
        Object.values(rightsPossible.territories)
          .flatMap((territory) => territory.timedtext)
          .map((item) => item.language)
      )
    ).sort((a, b) => a.localeCompare(b));
  }, [rightsPossible]);

  const regionRights = useMemo(() => {
    if (!rightsPossible) return [];
    return Object.keys(rightsPossible.territories).map((t) => t.toUpperCase());
  }, [rightsPossible]);

  const selectedRightsList = useMemo(() => selectedByTab[tab] ?? [], [selectedByTab, tab]);

  const utilizationCount = useMemo(() => {
    const audioCount = selectedByTab.audio.length;
    const territoriesCount = selectedByTab.territories.length;
    const timedTextCount = selectedByTab.timedtext.length;

    return audioCount + territoriesCount + timedTextCount;
  }, [selectedByTab.audio.length, selectedByTab.territories.length, selectedByTab.timedtext.length]);

  const utilizationMax = useMemo(() => {
    const audioMax = audioOptions?.length ?? 0;
    const timedTextMax = timedTextOptions?.length ?? 0;
    const territoriesMax = Object.values(territoryGrouping).reduce((acc, region) => acc + region.length, 0);

    return audioMax + timedTextMax + territoriesMax;
  }, [audioOptions, timedTextOptions]);

  useEffect(() => {
    if (!open) return;

    setTab(initialTab);
    setSelectedByTab({
      audio: uniq(initialSelectedByTab?.audio ?? []),
      territories: uniq(initialSelectedByTab?.territories ?? []),
      timedtext: uniq(initialSelectedByTab?.timedtext ?? [])
    });
  }, [initialSelectedByTab?.audio, initialSelectedByTab?.territories, initialSelectedByTab?.timedtext, initialTab, open]);

  const handleSave = () => {
    onSave?.(selectedByTab);
    onClose?.({ type: 'save' });
  };

  const handleChangeSelectedRights = (nextSelectedForTab: string[]) => {
    setSelectedByTab((prev) => ({
      ...prev,
      [tab]: nextSelectedForTab
    }));
  };

  return (
    <Modal maxWidth={1440} noHeader open={open} onClose={onClose} scrollDisabled title="Edit Config" onTransitionExited={onTransitionExited}>
      <StyledHeader>
        <StyledTitle variant="h2">Edit Config</StyledTitle>
        <StyledCloseButton onClick={onClose}>
          <StyledCloseIcon icon="close" />
        </StyledCloseButton>
      </StyledHeader>
      <StyledBody>
        <ScheduleAvailTypeTabs tab={tab} onChange={setTab} />
        <RightsUtilizationProgress utilizationCount={utilizationCount} utilizationMax={utilizationMax} />
        <RightsSelectionWrapper>
          <RightsSelectionSection
            tab={tab}
            selectedRightsList={selectedRightsList}
            rightsOptions={tab === 'audio' ? dubRights : tab === 'timedtext' ? subRights : regionRights}
            onChangeSelectedRights={handleChangeSelectedRights}
            scrollToElementName={scrollToElementName}
          />
        </RightsSelectionWrapper>
        <StyledFooter>
          <StyledCancelButton color="secondary" onClick={onClose} variant="outlined">
            Cancel
          </StyledCancelButton>
          <Button color="secondary" onClick={handleSave} variant="contained">
            Save
          </Button>
        </StyledFooter>
      </StyledBody>
    </Modal>
  );
};

export default NewScheduleAvailSelectionModal;

const StyledBody = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const StyledCancelButton = styled(Button)`
  color: ${({ theme }) => (theme as FocusTheme).palette.neutral[40]};
  border-color: ${({ theme }) => (theme as FocusTheme).palette.neutral[40]};
`;

const StyledCloseButton = styled(IconButton)`
  padding: 0;
`;

const StyledCloseIcon = styled(PolarisIcon)`
  font-size: 22px; // need to redefine in theme
  opacity: 0.6;
  color: ${({ theme }) => (theme as FocusTheme).palette.neutral[0]};

  &:hover {
    opacity: 1;
  }
`;

const StyledFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing(2)};
  padding-top: ${({ theme }) => theme.spacing(2)};
`;

const RightsSelectionWrapper = styled.div`
  padding-top: ${({ theme }) => theme.spacing(2)};
`;

const StyledHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: ${({ theme }) => theme.spacing(2)};
  margin-bottom: ${({ theme }) => theme.spacing(1)};
`;

const StyledTitle = styled(Typography)`
  font-size: 28px; // need to redefine in theme
  font-weight: 700;
  letter-spacing: -0.2px;
  color: ${({ theme }) => (theme as FocusTheme).palette.neutral[0]};
`;
